import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { meQuery } from '../lib/queries.js';

// Workspace-level navigation lives behind this badge. Click → dropdown
// with the org name + role at the top and links to every workspace
// surface (brands, team, activity, workspace settings, billing) gated
// by role. Rendered in both WorkspaceNav and TopNav so the entry point
// stays in the same place wherever the user is.
//
// Why a dropdown (not inline tabs): workspace-scoped pages and brand-
// scoped pages share the same header chrome, and the brand-scoped one
// already has its own tab row. Trying to render both would cram the
// header. The badge dropdown gives every nav surface one stable home
// for workspace-level destinations without competing for tab space.

type Role = 'owner' | 'admin' | 'member';

interface Item {
  key: 'brands' | 'team' | 'activity' | 'workspace' | 'billing';
  label: string;
  to: '/brands' | '/settings/team' | '/activity' | '/settings/workspace' | '/settings/billing';
  visibleTo?: ReadonlyArray<Role>;
}

const ITEMS: Item[] = [
  { key: 'brands', label: 'brands', to: '/brands' },
  // Team list is admin/owner only. Members don't need to see who else
  // is on the workspace — they collaborate via brand-scoped surfaces.
  { key: 'team', label: 'team', to: '/settings/team', visibleTo: ['owner', 'admin'] },
  { key: 'activity', label: 'activity', to: '/activity', visibleTo: ['owner', 'admin'] },
  { key: 'workspace', label: 'workspace', to: '/settings/workspace', visibleTo: ['owner'] },
  { key: 'billing', label: 'billing', to: '/settings/billing', visibleTo: ['owner'] },
];

export function WorkspaceBadge() {
  const { data: me } = useQuery(meQuery());
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!me?.workspaceName) return null;
  const role: Role = me.workspaceRole;
  const items = ITEMS.filter((i) => !i.visibleTo || i.visibleTo.includes(role));

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        // Same pill chrome as BrandSwitcher: white bg + faint border +
        // uppercase mono text. The `›` separator carries the parent →
        // child read on its own; the two pills can speak the same
        // visual language. Microanimations: subtle hover lift + border
        // darkens; chevron rotates 180° on open. Easing matches the
        // image-preview-modal's ease-out-quart curve.
        className={`group flex items-center gap-1.5 rounded-full border bg-white px-2.5 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/75 transition-all duration-150 ease-out hover:-translate-y-px hover:border-[var(--ink)]/40 hover:text-[var(--ink)] ${
          open ? 'border-[var(--ink)]/60' : 'border-[var(--line)]'
        }`}
      >
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--plum)] transition-transform duration-150 ease-out group-hover:scale-125" />
        {me.workspaceName.toLowerCase()}
        {/* Match BrandSwitcher's "↓" glyph for chevron consistency
            across the breadcrumb. Plain text arrow keeps the two pills
            speaking the same visual language. Rotates 180° on open so
            the affordance reads "menu is showing." */}
        <span
          aria-hidden="true"
          className={`text-[var(--ink)]/40 transition-transform duration-150 ease-out group-hover:translate-y-0.5 ${
            open ? 'rotate-180 text-[var(--ink)]/70' : ''
          }`}
        >
          ↓
        </span>
      </button>
      <AnimatePresence>
        {open ? (
          <motion.div
            role="menu"
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: 'top left' }}
            className="absolute left-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-lg border border-[var(--ink)] bg-white shadow-[4px_4px_0_var(--plum)]"
          >
            <div className="border-b border-[var(--line)] px-3 py-3">
              <p className="text-[13px] font-bold leading-tight text-[var(--ink)]">
                {me.workspaceName}
              </p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                <Trans>your role · {role}</Trans>
              </p>
            </div>
            <ul className="flex flex-col py-1">
              {items.map((item) => {
                const isActive = location.pathname === item.to;
                return (
                  <li key={item.key}>
                    <Link
                      to={item.to}
                      onClick={() => setOpen(false)}
                      role="menuitem"
                      className={`flex items-center justify-between px-3 py-2 text-[12.5px] no-underline transition-colors hover:bg-[var(--paper)] ${
                        isActive ? 'font-bold text-[var(--ink)]' : 'text-[var(--ink)]/85'
                      }`}
                    >
                      <span>↘ {item.label}</span>
                      {isActive ? (
                        <span
                          aria-hidden
                          className="inline-block h-1.5 w-1.5 rounded-full border-[1.2px] border-[var(--ink)] bg-[var(--spell)]"
                        />
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/** Slot helper for any nav that wants to render the badge without
 *  pulling the component module directly (avoids cyclic imports in
 *  shells that already pull headerish bits). */
export function WorkspaceBadgeSlot({ before, after }: { before?: ReactNode; after?: ReactNode }) {
  return (
    <>
      {before}
      <WorkspaceBadge />
      {after}
    </>
  );
}
